import axios from 'axios';

export type BreakdownType = {
  deviceTag: string;
  avg: number;
  total: number;
  kWp: number;
  co2Avg: number;
};

export type CategorizedType = {
  typeOfEnergy: string;
  avg: number;
  total: number;
  kWp: number;
  co2Avg: number;
  breakdown: BreakdownType[];
};

export type AveragesPerMonthType = {
  year: number;
  months: {
    month: number;
    excluded?: boolean;
    categorized: CategorizedType[];
  }[];
};

export type AveragesPerWeekType = {
  weekNumber: number;
  dayOfWeek: Record<
    string,
    {
      dayNumber: number;
      avg: number;
      total: number;
      kWp: number;
      co2Avg: number;
      categorized: CategorizedType[];
    }
  >;
};

export type NaiveBaselineType = {
  _id: string;
  _vendor: string;
  _building: string;
  _buildingName?: string;
  averagesPerMonth: AveragesPerMonthType[];
  averagesPerWeek: Record<string, AveragesPerWeekType>;
  createdAt?: string;
  updatedAt?: string;
  active?: boolean;
  locked?: boolean;
};

export type CreateNaiveBaselineData = {
  _vendor: string;
  _building: string;
  averagesPerMonth?: AveragesPerMonthType[];
  averagesPerWeek?: Record<string, AveragesPerWeekType>;
  active?: boolean;
  locked?: boolean;
};

export type UpdateNaiveBaselinePayload =
  | Partial<Omit<NaiveBaselineType, '_id' | 'createdAt' | 'updatedAt'>>
  | {
      excludedMonths: string[];
    };

export async function getNaiveBaselines(): Promise<NaiveBaselineType[]> {
  const response = await axios.get<NaiveBaselineType[]>(
    '/api/v1/naiveBaseline'
  );
  return response.data;
}

export async function getActiveNaiveBaselineByBuildingId(
  buildingId: string
): Promise<NaiveBaselineType> {
  const response = await axios.get<NaiveBaselineType>(
    `/api/v1/naiveBaseline/building/${buildingId}`
  );
  return response.data;
}

export async function getActiveNaiveBaselineByBuildings(
  buildingIds: string[],
  options: { summaryOnly?: boolean; monthlyOnly?: boolean } = {}
): Promise<NaiveBaselineType[]> {
  const response = await axios.post<NaiveBaselineType[]>(
    '/api/v1/naiveBaseline/buildings/active',
    { buildingIds, ...options }
  );

  return response.data;
}

export async function getNaiveBaselineById(
  baselineId: string
): Promise<NaiveBaselineType> {
  const response = await axios.get<NaiveBaselineType>(
    `/api/v1/naiveBaseline/${baselineId}`
  );
  return response.data;
}

export async function createNaiveBaseline(
  data: CreateNaiveBaselineData
): Promise<NaiveBaselineType> {
  const response = await axios.post<NaiveBaselineType>(
    '/api/v1/naiveBaseline',
    data
  );
  return response.data;
}

export async function updateNaiveBaseline(
  baselineId: string,
  data: UpdateNaiveBaselinePayload
): Promise<NaiveBaselineType> {
  const response = await axios.patch<NaiveBaselineType>(
    `/api/v1/naiveBaseline/${baselineId}`,
    data
  );
  return response.data;
}

export async function lockNaiveBaseline(
  baselineId: string,
  locked: boolean
): Promise<NaiveBaselineType> {
  const response = await axios.patch<NaiveBaselineType>(
    `/api/v1/naiveBaseline/${baselineId}/lock`,
    { locked }
  );
  return response.data;
}

export async function deleteNaiveBaseline(
  baselineId: string
): Promise<NaiveBaselineType> {
  const response = await axios.delete<NaiveBaselineType>(
    `/api/v1/naiveBaseline/${baselineId}`
  );
  return response.data;
}
